import { useTable } from '@/components/my-table/scripts/hooks';
import classes from './index.module.scss';
import MyTable from '@/components/my-table';
import { commonApis } from '@/configs/apis';
import type { MyProColumns } from '@/configs/theme/valueTypeMap';
import { createContext } from 'react';

export interface MainProps {
  className?: ClassNames;
}

const context = createContext<{
  users: any[];
  usersMap: Record<string, any>;
  refreshInterval: number;
  isRefresh: number;
}>({} as never);

const sendColumns = (usersMap: Record<string, any>) =>
  [
    {
      title: 'msg_seq',
      dataIndex: 'msg_seq',
      width: 100,
    },
    {
      title: 'message_id',
      dataIndex: 'message_id',
      width: 100,
    },
    {
      title: 'payload',
      dataIndex: 'payload',
      width: 100,
    },
    {
      title: 'payload_type',
      dataIndex: 'payload_type',
      width: 100,
    },
    {
      title: 'arrive_receipt_count',
      dataIndex: 'arrive_receipt_count',
      width: 100,
    },
    {
      title: 'read_receipt_count',
      dataIndex: 'read_receipt_count',
      width: 100,
    },
    {
      title: 'sender_id',
      dataIndex: 'sender_id',
      width: 100,
      render(dom: string) {
        return usersMap[dom]?.label || dom;
      },
    },
    {
      title: 'receiver_id',
      dataIndex: 'receiver_id',
      width: 100,
      render(dom: string) {
        return usersMap[dom]?.label || dom;
      },
    },
    {
      title: 'created_at',
      dataIndex: 'created_at',
      valueType: 'dateTime',
    },
  ] as MyProColumns[];

const noticeColumns = [
  {
    title: 'sequence',
    dataIndex: 'sequence',
    width: 100,
  },
  {
    title: 'notice_type',
    dataIndex: 'notice_type',
    width: 100,
  },
  {
    title: 'online',
    dataIndex: 'is_offline',
    render(v: any) {
      return <div>{v ? 'No' : 'Yes'}</div>;
    },
    width: 100,
  },
  {
    title: 'created_at',
    dataIndex: 'created_at',
    valueType: 'dateTime',
  },
] as MyProColumns[];

const pullColumns = [
  {
    title: 'sequence',
    dataIndex: 'sequence',
    width: 100,
  },
  {
    title: 'pull_type',
    dataIndex: 'pull_type',
    width: 100,
  },
  {
    title: 'res_max_seq',
    dataIndex: 'res_max_seq',
    width: 100,
  },
  {
    title: 'created_at',
    dataIndex: 'created_at',
    valueType: 'dateTime',
  },
] as MyProColumns[];

export default function Main({ className }: MainProps) {
  const { data: users = [], run } = useRequest(
    () =>
      commonApis.userList().then((res) => {
        return (res.data as any[]).map(
          ({ name, id }: { name: string; id: string }) => ({
            label: name + ` -- (${id})`,
            value: id,
          })
        );
      }),
    {
      manual: true,
    }
  );

  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [isRefresh, setRefreshFlag] = useState(0);
  const [refreshInterval_, setRefreshInterval_] = useState(refreshInterval);

  const usersMap = useMemo(() => {
    const map: Record<string, any> = {};
    users.forEach((item) => {
      map[item.value] = item;
    });
    return map;
  }, [users]);

  const refresh = useCallback(() => {
    setRefreshFlag((p) => p + 1);
  }, []);

  useEffect(() => {
    run();
  }, [isRefresh, run]);

  return (
    <div className={cls(classes.main, className)}>
      <context.Provider
        value={{ users, usersMap, refreshInterval: refreshInterval_, isRefresh }}
      >
        <Tabs
          size="small"
          type="card"
          defaultActiveKey="0"
          items={[
            {
              label: 'Users',
              key: '0',
              children: <UsersTables></UsersTables>,
            },
            {
              label: 'Group',
              key: '1',
              children: <GroupTables></GroupTables>,
            },
            {
              label: 'Tools',
              key: '2',
              children: <DebugTools></DebugTools>,
            },
          ]}
          tabBarExtraContent={
            <Space align="center" style={{ padding: '0 10px' }}>
              <Button size="small" type="primary" onClick={refresh}>
                Refresh All
              </Button>
              <Input
                value={refreshInterval}
                size="small"
                type="number"
                prefix={'Auto refresh:'}
                suffix="ms"
                onChange={(e) => {
                  setRefreshInterval(Number(e.target.value));
                }}
                onBlur={() => {
                  setRefreshInterval_(refreshInterval);
                }}
              ></Input>
            </Space>
          }
        />
      </context.Provider>
    </div>
  );
}

interface DebugToolsValues {
  singleCount: number;
  singleOpen: boolean;
  singleNumber: number;
  groupCount: number;
  groupId: string;
  groupOpen: boolean;
  groupNumber: number;
}

function DebugTools() {
  const { isRefresh } = useContext(context);
  const { data: groups = [], run } = useRequest(
    () =>
      commonApis.groupList().then((res) => {
        return (res.data as any[]).map(
          ({ name, id }: { name: string; id: string }) => ({
            label: name + ` -- ${id}`,
            value: id,
          })
        );
      }),
    {
      manual: true,
    }
  );

  useEffect(() => {
    run();
  }, [isRefresh, run]);

  return (
    <div style={{ padding: 10 }}>
      <BetaSchemaForm
        size="small"
        layout="inline"
        initialValues={{
          singleCount: 10,
          singleOpen: false,
          singleNumber: 100,
        }}
        onFinish={async (values: DebugToolsValues) => {
          await Promise.all([
            commonApis.sendSingle({
              open: values.singleOpen ? 1 : 0,
              count: values.singleCount,
              msgCount: values.singleNumber <= 0 ? undefined : values.singleNumber,
            }),
          ]);
          return true;
        }}
        columns={[
          {
            title: '单聊消息',
            valueType: 'group',
            columns: [
              {
                title: '开启',
                valueType: 'switch',
                dataIndex: 'singleOpen',
              },
              {
                title: '人数',
                valueType: 'digit',
                dataIndex: 'singleCount',
              },
              {
                title: '发送条数',
                valueType: 'digit',
                dataIndex: 'singleNumber',
                formItemProps: {
                  tooltip: '"0"不限制条数',
                },
              },
            ],
          },
        ]}
      />
      <BetaSchemaForm
        size="small"
        layout="inline"
        initialValues={{
          groupCount: 10,
          groupId: '',
          groupOpen: false,
          groupNumber: 100,
        }}
        onFinish={async (values: DebugToolsValues) => {
          await Promise.all([
            commonApis.sendGroup({
              groupId: values.groupId,
              count: values.groupCount,
              open: values.groupOpen ? 1 : 0,
              msgCount: values.groupNumber <= 0 ? undefined : values.groupNumber,
            }),
          ]);
          return true;
        }}
        columns={[
          {
            title: '群聊消息',
            valueType: 'group',
            columns: [
              {
                title: '开启',
                valueType: 'switch',
                dataIndex: 'groupOpen',
              },
              {
                title: '群组',
                valueType: 'select',
                dataIndex: 'groupId',
                fieldProps: {
                  showSearch: true,
                  options: [
                    { label: '[ Create a new group ]', value: 'GN_create_group' },
                    ...groups,
                  ],
                  popupMatchSelectWidth: false,
                },
                formItemProps: {
                  rules: [{ required: true }],
                },
              },
              {
                title: '人数',
                valueType: 'digit',
                dataIndex: 'groupCount',
              },
              {
                title: '发送条数',
                valueType: 'digit',
                dataIndex: 'groupNumber',
                formItemProps: {
                  tooltip: '"0"不限制条数',
                },
              },
            ],
          },
        ]}
      />
    </div>
  );
}

function UsersTables({ className }: MainProps) {
  const [values, setValues] = useState<{ users: string[] }>({
    users: [],
  });

  const { users, usersMap } = useContext(context);

  const items = useMemo(() => {
    return {
      users: users ? values.users.map((id) => usersMap[id]) : [],
    };
  }, [users, values.users, usersMap]);

  return (
    <div className={cls(classes.main, className)}>
      <div style={{ padding: '0 10px 10px' }}>
        <BetaSchemaForm
          size="small"
          layout="inline"
          onFinish={async (values: any) => {
            setValues({
              users: [],
              ...values,
            });
          }}
          columns={[
            {
              title: 'Users',
              valueType: 'select',
              dataIndex: 'users',
              formItemProps: {
                style: {
                  minWidth: 500,
                },
              },
              fieldProps: {
                mode: 'multiple',
                showSearch: true,
                options: users,
                popupMatchSelectWidth: false,
                maxTagCount: 3,
              },
            },
          ]}
        />
      </div>
      <div>
        {items.users.map((item) => {
          return <UserTables key={item.value} item={item}></UserTables>;
        })}
      </div>
    </div>
  );
}

function GroupTables({ className }: MainProps) {
  const { isRefresh } = useContext(context);
  const [values, setValues] = useState<{ groups: string[] }>({
    groups: [],
  });

  const { data: groups, run } = useRequest(
    () =>
      commonApis.groupList().then((res) => {
        return (res.data as any[]).map(
          ({ name, id }: { name: string; id: string }) => ({
            label: name + ` -- ${id}`,
            value: id,
          })
        );
      }),
    {
      manual: true,
    }
  );

  const items = useMemo(() => {
    return {
      groups: groups
        ? values.groups.map((id) => groups.find(({ value }) => id === value)!)
        : [],
    };
  }, [groups, values.groups]);

  useEffect(() => {
    run();
  }, [isRefresh, run]);

  return (
    <div className={cls(classes.main, className)}>
      <div style={{ padding: '0 10px' }}>
        <BetaSchemaForm
          size="small"
          layout="inline"
          onFinish={async (values: any) => {
            setValues({
              groups: [],
              ...values,
            });
          }}
          columns={[
            {
              title: 'Groups',
              valueType: 'select',
              dataIndex: 'groups',
              formItemProps: {
                style: {
                  minWidth: 500,
                },
              },
              fieldProps: {
                mode: 'multiple',
                showSearch: true,
                options: groups,
                popupMatchSelectWidth: false,
                maxTagCount: 3,
              },
            },
          ]}
        />
      </div>
      <div>
        {items.groups.map((item) => {
          return <GroupSendTable key={item.value} item={item}></GroupSendTable>;
        })}
      </div>
    </div>
  );
}

function UserTables({ item }: any) {
  const { usersMap, isRefresh } = useContext(context);

  const [values, setValues] = useState<{ chats: string[] }>({
    chats: [],
  });

  const { data: chats, run } = useRequest(() =>
    commonApis.chatList({ id: item.value }).then((res) => {
      return res.data as any[];
    })
  );

  const chats_ = useMemo(() => {
    return chats
      ? chats
          .map((id) => {
            const user = usersMap[id.slice(2)];
            return (
              user && {
                label: user.label,
                value: id,
              }
            );
          })
          .filter(Boolean)
      : [];
  }, [chats, usersMap]);

  const items = useMemo(() => {
    return {
      chats: chats_
        ? values.chats.map((id) => chats_.find(({ value }) => id === value)!)
        : [],
    };
  }, [chats_, values.chats]);

  useEffect(() => {
    run();
  }, [isRefresh, run]);

  return (
    <FullBox
      column
      frontChildren={
        <div style={{ padding: '0 10px', backgroundColor: 'var(--blue-2)' }}>
          {item.label}
        </div>
      }
    >
      <div style={{ display: 'flex' }}>
        <FullBox
          frontChildren={
            <div style={{ padding: '5 0' }}>
              <BetaSchemaForm
                size="small"
                layout="inline"
                onFinish={async (values: any) => {
                  setValues({
                    chats: [],
                    ...values,
                  });
                }}
                columns={[
                  {
                    renderFormItem() {
                      return '发送消息列表';
                    },
                  },
                  {
                    title: 'Chats',
                    valueType: 'select',
                    dataIndex: 'chats',
                    formItemProps: {
                      style: {
                        minWidth: 500,
                      },
                    },
                    fieldProps: {
                      mode: 'multiple',
                      showSearch: true,
                      options: chats_,
                      popupMatchSelectWidth: false,
                      maxTagCount: 3,
                    },
                  },
                ]}
              />
            </div>
          }
          column
          style={{
            flex: '1 1 auto',
            backgroundColor: 'var(--purple-2)',
            padding: 1,
          }}
        >
          {items.chats.map((chat) => {
            return (
              <UserSendTable key={chat.value} item={item} chat={chat}></UserSendTable>
            );
          })}
        </FullBox>
        <FullBox
          frontChildren={<div style={{ height: 25 }}>消息通知</div>}
          column
          style={{
            width: '430px',
            flex: '0 0 auto',
            backgroundColor: 'var(--cyan-2)',
            padding: 1,
          }}
        >
          <UserNoticeTable item={item}></UserNoticeTable>
        </FullBox>
        <FullBox
          frontChildren={<div style={{ height: 25 }}>消息拉取</div>}
          column
          style={{
            width: '430px',
            flex: '0 0 auto',
            backgroundColor: 'var(--lime-2)',
            padding: 1,
          }}
        >
          <UserPullTable item={item}></UserPullTable>
        </FullBox>
      </div>
    </FullBox>
  );
}

function UserSendTable({ item, chat }: any) {
  const { usersMap, isRefresh, refreshInterval } = useContext(context);
  const { tableProps, load } = useTable({
    immediate: false,
    request({ current, pageSize }) {
      return commonApis
        .sendList({
          id: item.value,
          c_id: chat.value,
          page: current,
          size: pageSize,
        })
        .then((res) => ({
          data: res.data.list,
          total: res.data.count,
        }));
    },
  });

  useEffect(() => {
    const id = setInterval(() => {
      load();
    }, refreshInterval);
    return () => {
      clearInterval(id);
    };
  }, [load, refreshInterval]);

  useEffect(() => {
    load();
  }, [isRefresh, load]);

  return (
    <MyTable
      {...tableProps}
      title={() => chat.label}
      loading={false}
      rowKey={(r) => r._id.$oid}
      size="small"
      columns={sendColumns(usersMap)}
      paginationProps={{
        ...tableProps.paginationProps,
        size: 'small',
      }}
    ></MyTable>
  );
}

function UserNoticeTable({ item }: any) {
  const { isRefresh, refreshInterval } = useContext(context);
  const { tableProps, load } = useTable({
    immediate: false,
    request({ current, pageSize }) {
      return commonApis
        .noticeList({
          id: item.value,
          page: current,
          size: pageSize,
        })
        .then((res) => ({
          data: res.data.list,
          total: res.data.count,
        }));
    },
  });

  useEffect(() => {
    const id = setInterval(() => {
      load();
    }, refreshInterval);
    return () => {
      clearInterval(id);
    };
  }, [load, refreshInterval]);

  useEffect(() => {
    load();
  }, [isRefresh, load]);

  return (
    <MyTable
      {...tableProps}
      loading={false}
      rowKey={(r) => r._id.$oid}
      size="small"
      columns={noticeColumns}
      paginationProps={{
        ...tableProps.paginationProps,
        size: 'small',
      }}
    ></MyTable>
  );
}

function UserPullTable({ item }: any) {
  const { isRefresh, refreshInterval } = useContext(context);
  const { tableProps, load } = useTable({
    immediate: false,
    request({ current, pageSize }) {
      return commonApis
        .pullList({
          id: item.value,
          page: current,
          size: pageSize,
        })
        .then((res) => ({
          data: res.data.list,
          total: res.data.count,
        }));
    },
  });

  useEffect(() => {
    const id = setInterval(() => {
      load();
    }, refreshInterval);
    return () => {
      clearInterval(id);
    };
  }, [load, refreshInterval]);

  useEffect(() => {
    load();
  }, [isRefresh, load]);

  return (
    <MyTable
      {...tableProps}
      loading={false}
      rowKey={(r) => r._id.$oid}
      size="small"
      columns={pullColumns}
      paginationProps={{
        ...tableProps.paginationProps,
        size: 'small',
      }}
    ></MyTable>
  );
}

function GroupSendTable({ item }: any) {
  const { usersMap, isRefresh, refreshInterval } = useContext(context);
  const { tableProps, load } = useTable({
    immediate: false,
    request({ current, pageSize }) {
      return commonApis
        .sendList({
          id: item.value,
          c_id: item.value,
          page: current,
          size: pageSize,
        })
        .then((res) => ({
          data: res.data.list,
          total: res.data.count,
        }));
    },
  });

  useEffect(() => {
    const id = setInterval(() => {
      load();
    }, refreshInterval);
    return () => {
      clearInterval(id);
    };
  }, [load, refreshInterval]);

  useEffect(() => {
    load();
  }, [isRefresh, load]);

  return (
    <MyTable
      {...tableProps}
      title={() => item.label}
      loading={false}
      rowKey={(r) => r._id.$oid}
      size="small"
      columns={sendColumns(usersMap)}
      paginationProps={{
        ...tableProps.paginationProps,
        size: 'small',
      }}
    ></MyTable>
  );
}
